import type { CoachProfile } from '@efdigital/ll-addon-data';
import { queryCoachProfileByUserId } from '@efdigital/ll-xceed-v2/data/builders/coaching/coach-profiles';
import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type { Store } from '@warp-drive/core';

import type {
  CoachingCoacheeCohortModel,
  CoachingCoacheeCohortPathParams,
} from '../../cohort';

export type CoachingCoacheeCohortSessionsBookChemistryModel =
  CoachingCoacheeCohortModel & {
    coach: CoachProfile;
  };

export default class CoachingCoacheeCohortSessionsBookChemistryRoute extends Route<CoachingCoacheeCohortSessionsBookChemistryModel> {
  @service declare store: Store;
  @service declare router: RouterService;

  /**
   * Coaching sessions can only be booked with a chosen coach. Without one,
   * send the coachee to pick a coach rather than throwing from `model()`,
   * which surfaced as the cohort error page ("You are not registered as a
   * coachee") for a coachee who simply hadn't chosen yet.
   */
  beforeModel(): void {
    const parentModel = this.modelFor(
      'coaching.coachee.cohort'
    ) as CoachingCoacheeCohortModel;
    if (parentModel.planner.chosenCoachPartnership()) {
      return;
    }

    const { program_id, cohort_id } = this.paramsFor(
      'coaching.coachee.cohort'
    ) as CoachingCoacheeCohortPathParams;
    this.router.replaceWith(
      'coaching.coachee.cohort.coaches',
      program_id,
      cohort_id
    );
  }

  async model(): Promise<CoachingCoacheeCohortSessionsBookChemistryModel> {
    const parentModel = this.modelFor(
      'coaching.coachee.cohort'
    ) as CoachingCoacheeCohortModel;

    const chosenCoachPartnership = parentModel.planner.chosenCoachPartnership();
    if (!chosenCoachPartnership) {
      throw new Error('No chosen coach partnership found');
    }

    const coachUserId = chosenCoachPartnership.coachUserId;

    const coaches = await this.store.request(
      queryCoachProfileByUserId(coachUserId, 'published')
    );
    const coach = coaches.content.data.find((c) => c.user === coachUserId);

    if (!coach) {
      throw new Error('Coach not found');
    }

    return {
      ...parentModel,
      coach,
    };
  }
}
